// Embed Authentication Service
// Handles authentication for embedded chatbot widgets

import { EMBED_CONFIG } from '../configs/EmbedConfig';
import { v4 as uuidv4 } from 'uuid';

class EmbedAuthService {
  constructor() {
    this.authToken = null;
    this.authMethod = EMBED_CONFIG.AUTH.DEFAULT_METHOD;
    this.rateLimitTracker = new Map();
  }

  /**
   * Initialize authentication for the embed widget
   * @param {Object} config - Authentication configuration
   * @returns {Promise<Object>} Authentication result
   */
  async initialize(config = {}) {
    try {
      const {
        method = EMBED_CONFIG.AUTH.DEFAULT_METHOD,
        token = null,
        chat_agent_id = null,
        chat_id = null
      } = config;

      this.authMethod = method;

      // Only support JWT token authentication for now
      if (method !== EMBED_CONFIG.AUTH.METHODS.TOKEN) {
        throw new Error('Only JWT token authentication is supported');
      }

      // Initialize authentication with the provided token

      // Allow initialization without token - token will be validated when making API calls
      if (token) {
        const result = await this.authenticateWithToken(token);
        return result;
      } else {
        console.log('Initialized embed without token - API calls will require token');

        return {
          success: true,
          chat_agent_id: chat_agent_id,
          chat_id: chat_id,
          method: EMBED_CONFIG.AUTH.METHODS.TOKEN,
          requiresTokenForAPI: true
        };
      }
    } catch (error) {
      console.error('Embed authentication failed:', error);
      return {
        success: false,
        error: error.message,
        sessionId: null,
        authToken: null
      };
    }
  }

  /**
   * Authenticate using JWT token
   */
  async authenticateWithToken(token) {
    if (!token) {
      throw new Error('Token is required for token authentication');
    }

    // Validate token format
    if (!this.isValidJWT(token)) {
      throw new Error('Invalid JWT token format');
    }

    // Store token
    this.authToken = token;
    
    // Generate a chat_id if needed
    if (!this.chat_id) {
      this.chat_id = this.generateChatId();
    }

    // Store in session storage for persistence
    this.storeAuthData({
      method: EMBED_CONFIG.AUTH.METHODS.TOKEN,
      token: token,
      chat_id: this.chat_id,
      timestamp: Date.now()
    });

    return {
      success: true,
      chat_id: this.chat_id,
      authToken: this.authToken,
      method: EMBED_CONFIG.AUTH.METHODS.TOKEN
    };
  }

  /**
   * Authenticate using API key
   */
  async authenticateWithApiKey(apiKey) {
    if (!apiKey) {
      throw new Error('API key is required for API key authentication');
    }

    // Generate chat_id if needed
    if (!this.chat_id) {
      this.chat_id = this.generateChatId();
    }

    // Store API key securely
    this.storeAuthData({
      method: EMBED_CONFIG.AUTH.METHODS.API_KEY,
      apiKey: apiKey,
      chat_id: this.chat_id,
      timestamp: Date.now()
    });

    return {
      success: true,
      chat_id: this.chat_id,
      apiKey: apiKey,
      method: EMBED_CONFIG.AUTH.METHODS.API_KEY
    };
  }

  /**
   * Authenticate for public agent access
   */
  async authenticatePublic(chat_agent_id) {
    if (!chat_agent_id) {
      throw new Error('Chat Agent ID is required for public authentication');
    }

    // Generate chat_id if needed
    if (!this.chat_id) {
      this.chat_id = this.generateChatId();
    }

    // Store public access data
    this.storeAuthData({
      method: EMBED_CONFIG.AUTH.METHODS.PUBLIC,
      chat_agent_id: chat_agent_id,
      chat_id: this.chat_id,
      timestamp: Date.now()
    });

    return {
      success: true,
      chat_id: this.chat_id,
      chat_agent_id: chat_agent_id,
      method: EMBED_CONFIG.AUTH.METHODS.PUBLIC
    };
  }

  /**
   * Authenticate anonymously
   */
  async authenticateAnonymous(providedChatId = null, allowAnonymous = true) {
    if (!allowAnonymous) {
      throw new Error('Anonymous authentication is not allowed');
    }

    // Use provided chat ID or generate new one
    this.chat_id = providedChatId || this.generateChatId();

    // Store anonymous session data
    this.storeAuthData({
      method: EMBED_CONFIG.AUTH.METHODS.ANONYMOUS,
      chat_id: this.chat_id,
      timestamp: Date.now()
    });

    return {
      success: true,
      chat_id: this.chat_id,
      method: EMBED_CONFIG.AUTH.METHODS.ANONYMOUS
    };
  }

  /**
   * Get authentication headers for API requests
   */
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420'
    };

    switch (this.authMethod) {
      case EMBED_CONFIG.AUTH.METHODS.TOKEN:
        if (this.authToken) {
          headers['Authorization'] = `Bearer ${this.authToken}`;
        }
        break;
      
      case EMBED_CONFIG.AUTH.METHODS.API_KEY: {
        const authData = this.getStoredAuthData();
        if (authData?.apiKey) {
          headers['X-API-Key'] = authData.apiKey;
        }
        break;
      }
      
      // Public and anonymous don't need special headers
      default:
        break;
    }

    return headers;
  }

  /**
   * Check if user is rate limited
   */
  checkRateLimit(identifier = 'default') {
    const now = Date.now();
    const windowStart = now - 60000; // 1 minute window
    
    if (!this.rateLimitTracker.has(identifier)) {
      this.rateLimitTracker.set(identifier, []);
    }
    
    const requests = this.rateLimitTracker.get(identifier);
    
    // Remove old requests outside the window
    const recentRequests = requests.filter(timestamp => timestamp > windowStart);
    this.rateLimitTracker.set(identifier, recentRequests);
    
    // Check if rate limit exceeded
    if (recentRequests.length >= EMBED_CONFIG.SECURITY.RATE_LIMIT.MESSAGES_PER_MINUTE) {
      return {
        allowed: false,
        resetTime: windowStart + 60000
      };
    }
    
    // Add current request
    recentRequests.push(now);
    this.rateLimitTracker.set(identifier, recentRequests);
    
    return {
      allowed: true,
      remaining: EMBED_CONFIG.SECURITY.RATE_LIMIT.MESSAGES_PER_MINUTE - recentRequests.length
    };
  }

  /**
   * Validate JWT token format
   */
  isValidJWT(token) {
    const parts = token.split('.');
    return parts.length === 3;
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    console.log('🚨 WARNING: Generating new embed sessionId - this should only happen if no sessionId was provided');
    return `embed_${uuidv4()}`;
  }

  /**
   * Generate unique chat ID
   */
  generateChatId() {
    console.log('🚨 WARNING: Generating new embed chat_id - this should only happen if no chat_id was provided');
    return `chat_${uuidv4()}`;
  }

  /**
   * Store authentication data in session storage
   */
  storeAuthData(data) {
    try {
      const storageData = {
        ...data,
        expiresAt: Date.now() + EMBED_CONFIG.AUTH.SESSION_DURATION
      };
      
      sessionStorage.setItem(
        EMBED_CONFIG.AUTH.STORAGE_KEY,
        JSON.stringify(storageData)
      );
    } catch (error) {
      console.warn('Failed to store auth data:', error);
    }
  }

  /**
   * Get stored authentication data
   */
  getStoredAuthData() {
    try {
      const stored = sessionStorage.getItem(EMBED_CONFIG.AUTH.STORAGE_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);
      
      // Check if expired
      if (data.expiresAt && Date.now() > data.expiresAt) {
        this.clearAuthData();
        return null;
      }

      return data;
    } catch (error) {
      console.warn('Failed to get stored auth data:', error);
      return null;
    }
  }

  /**
   * Clear authentication data
   */
  clearAuthData() {
    try {
      sessionStorage.removeItem(EMBED_CONFIG.AUTH.STORAGE_KEY);
      this.sessionId = null;
      this.authToken = null;
    } catch (error) {
      console.warn('Failed to clear auth data:', error);
    }
  }

  /**
   * Get current session information
   */
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      authMethod: this.authMethod,
      isAuthenticated: !!this.sessionId,
      authToken: this.authToken
    };
  }

  /**
   * Refresh authentication if needed
   */
  async refreshAuth() {
    const storedData = this.getStoredAuthData();
    if (!storedData) {
      return { success: false, error: 'No stored authentication data' };
    }

    // Re-initialize with stored data
    return await this.initialize({
      method: storedData.method,
      token: storedData.token,
      apiKey: storedData.apiKey,
      agentId: storedData.agentId,
      sessionId: storedData.sessionId
    });
  }
}

// Create singleton instance
const embedAuthService = new EmbedAuthService();

export default embedAuthService;
